import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  ListGroup,
  ListGroupItem,
  Input,
  FormGroup,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';

import { getSubScriptionsFeatures, upgradeSubscription } from '../../../axios/subscription';
import { useAuthStore } from '../../../zustand-store/auth.store';

// Pricing Card Component
const PricingOption = ({
  id,
  title,
  price,
  currency,
  features = [],
  buttonText,
  isHighlighted = false,
  onLearnMore,
  toggleText,
  type,
  currentPlan,
  onUpgrade
}) => {
  const navigate = useNavigate();

  const handleSelect = () => {
    if (currentPlan === type) return; // Already selected
    if (type === 'Pro') onUpgrade(id);
    else if (price === 0) navigate(`/register?planId=${id}`);
    else navigate(`/signup?planId=${id}`);
  };

  return (
    <Card
      className={`h-100 shadow-sm border-1 ${isHighlighted ? 'border-dark border-2' : ''}`}
      style={{ borderRadius: '1rem', transition: 'all 0.3s ease' }}
    >
      <CardBody className="d-flex flex-column align-items-center text-center p-4">
        <h3 className="fw-bold mb-2">{title}</h3>
        <h1 className="fw-bold mb-2">
          {currency}
          {price}
          <small className="fs-6 text-muted"> / Location / month</small>
        </h1>

        <div style={{ minHeight: '50px' }}>{toggleText}</div>
        <Button
          color={currentPlan === type ? 'light' : 'outline-dark'}
          className={`w-100 mb-3 fw-semibold hover-black-btn ${currentPlan === type && 'btn-dark'}`}
          style={{ borderRadius: '8px', padding: '10px 0', fontSize: '1.1rem' }}
          onClick={handleSelect}
          // disabled={currentPlan === type}
        >
          {currentPlan === type ? 'Selected' : buttonText}
        </Button>

        {/* <div className="d-flex align-items-center justify-content-center bg-light rounded-pill px-3 py-2 mb-3">
          <small className="text-success fw-bold">24/7 HUMAN SUPPORT</small>
          <i className="bx bx-smile ms-2 text-success"></i>
        </div> */}

        <ListGroup flush className="text-start w-100">
          {features.map((feature, idx) => (
            <ListGroupItem key={idx} className="border-0 px-0 py-1 d-flex align-items-center">
              <i className="bx bx-check text-dark me-2" style={{ fontSize: 20 }}></i>
              {feature.name}
            </ListGroupItem>
          ))}
        </ListGroup>
      </CardBody>
    </Card>
  );
};

// Upgrade Pricing Modal
export default function PlanUpgrade({ isOpen, onClose }) {
  const [isAnnual, setIsAnnual] = useState(false);
  const [pricingOptions, setPricingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { currentPlan, id } = useAuthStore((s) => s);

  const [showPayment, setShowPayment] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [seats, setSeats] = useState(1);
  const [planUpgraded, setPlanUpgraded] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await getSubScriptionsFeatures();

        if (res?.data?.data) setPricingOptions(res.data.data);
      } catch (err) {
        console.error('Error fetching plans', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getCurrencySymbol = (c) => {
    if (c === 'GBP') return '£';
    if (c === 'EUR') return '€';
    return '$';
  };

  const getPrice = (plan) => {
    const amount = isAnnual ? plan.priceAnnual : plan.priceMonthly;
    const symbol = getCurrencySymbol(plan.currency);
    return { amount, currency: symbol };
  };

  const handleCardToken = async (token, buyerVerificationToken) => {
    try {
      setLoading(true);

      const payload = {
        userId: id,
        subscriptionPlanId: selectedPlanId,
        seats: seats,
        sourceId: token.token,
        buyerVerificationToken: buyerVerificationToken.token,
        cadence: isAnnual ? 'Annual' : 'Monthly'
      };
      console.log('Upgrade Payload:', payload);

      const res = await upgradeSubscription(payload);

      if (res?.data?.message == 'success') {
        toast.success('Subscription upgraded!');
        setShowPayment(false);
        setPlanUpgraded(true);
      } else {
        toast.error('Upgrade failed.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Payment error occurred');
    } finally {
      setLoading(false);
    }
  };

  const allFeatures = Array.from(
    new Set(pricingOptions.flatMap((p) => p.features.map((f) => f.name)))
  );
  const handleInputEvent = (event) => {
    const isValid = event?.detail?.currentState?.isCompletelyValid === true;
    console.log(isValid, event);
    setIsCardValid(isValid);
  };

  return (
    <Container fluid style={{ background: '#F2F2F2' }}>
      <h3
        className="fw-bold mb-0 text-center"
        style={{ fontSize: 24, fontWeight: 700, color: '#000' }}
      >
        Unlock the tools that help you grow faster
      </h3>
      <p
        className="text-center   m-0 pb-2"
        style={{ fontSize: 13, fontWeight: 400, color: '#000' }}
      >
        Go Pro and Power Your Business
      </p>
      <ModalBody className=" py-4">
        <Container fluid>
          {loading ? (
            <div className="text-center my-5">
              <Spinner color="dark" style={{ width: '3rem', height: '3rem' }} />
            </div>
          ) : (
            <>
              {!showPayment && !planUpgraded && (
                <Row className="justify-content-center">
                  {pricingOptions.map((plan) => {
                    const { amount, currency } = getPrice(plan);

                    return (
                      <Col md="5" lg="4" className="mb-4" key={plan.id}>
                        <PricingOption
                          id={plan.id}
                          title={plan.name}
                          price={amount}
                          currency={currency}
                          features={plan.features}
                          buttonText={amount === 0 ? 'Start FREE' : 'Start Pro'}
                          isHighlighted={plan.type === 'Pro'}
                          type={plan.type}
                          currentPlan={currentPlan}
                          onLearnMore={setSelectedPlan}
                          onUpgrade={(planId) => {
                            setSelectedPlanId(planId);
                            setShowPayment(true);
                          }}
                          toggleText={
                            plan.priceMonthly !== 0 && (
                              <div className="d-flex justify-content-center align-items-center mb-4">
                                <span className="me-2 fw-semibold">Monthly</span>
                                <FormGroup switch className="m-0">
                                  <Input
                                    type="switch"
                                    checked={isAnnual}
                                    onClick={() => setIsAnnual(!isAnnual)}
                                  />
                                </FormGroup>
                                <span className="ms-2 fw-semibold">Annual</span>
                              </div>
                            )
                          }
                        />
                      </Col>
                    );
                  })}
                </Row>
              )}

              {/* Payment Section */}
              {showPayment && !planUpgraded && selectedPlanId && (
                <div className="p-4 ">
                  {/* Tier and Price Info */}
                  {pricingOptions
                    .filter((p) => p.id === selectedPlanId)
                    .map((plan) => {
                      const amount = isAnnual ? plan.priceAnnual : plan.priceMonthly;
                      const currency = getCurrencySymbol(plan.currency);
                      const totalPrice = (amount * seats).toFixed(2);
                      const monthlyPrice = plan.priceMonthly;
                      const annualPrice = plan.priceAnnual;
                      // Calculate discount percentage dynamically based on your values
                      const discount = (
                        ((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) *
                        100
                      )?.toFixed(2);

                      return (
                        <div
                          key={plan.id}
                          className="mb-4 text-center bg-white p-5  border bg-white"
                          style={{ borderRadius: 10, overflow: 'hidden' }}
                        >
                          <h4 className="fw-bold mb-1">{plan.name} Plan</h4>

                          <div className="d-flex justify-content-center mt-4 gap-4 mb-3">
                            {/* Monthly Option */}
                            <label className="d-flex align-items-center cursor-pointer">
                              <input
                                type="radio"
                                name="billingCycle"
                                value="monthly"
                                checked={!isAnnual}
                                onClick={() => setIsAnnual(false)}
                                className="form-check-input"
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <span className="ms-2 fw-semibold">Monthly</span>
                            </label>

                            {/* Annual Option */}
                            <label className="d-flex align-items-center cursor-pointer">
                              <input
                                type="radio"
                                name="billingCycle"
                                value="annual"
                                checked={isAnnual}
                                onClick={() => setIsAnnual(true)}
                                className="form-check-input"
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <span className="ms-2 fw-semibold">
                                Annual
                                <span style={{ color: 'red', marginLeft: 6 }}>
                                  ({discount}% off)
                                </span>
                              </span>
                            </label>
                          </div>

                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <p style={{ fontSize: 13, color: '#000' }}>
                                {plan.name} {`(Per location)`}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: 13, color: '#000' }}>
                                {' '}
                                {currency}
                                {amount}
                              </p>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <p style={{ fontSize: 13, color: '#000' }}>{'Number of Locations'}</p>
                            </div>
                            <div
                              className="d-flex justify-content-between align-items-center"
                              style={{ gap: '8px' }}
                            >
                              {/* Minus button */}
                              <i
                                className="bx bx-minus-circle"
                                style={{ fontSize: 16, color: '#000', cursor: 'pointer' }}
                                onClick={() => {
                                  if (seats > 1) setSeats(seats - 1);
                                }}
                              />

                              {/* Current value */}
                              <p className="m-0" style={{ fontSize: 13, color: '#000' }}>
                                {seats}
                              </p>

                              {/* Plus button */}
                              <i
                                className="bx bx-plus-circle"
                                style={{ fontSize: 16, color: '#000', cursor: 'pointer' }}
                                onClick={() => {
                                  if (seats < 20) setSeats(seats + 1);
                                }}
                              />
                            </div>
                          </div>
                          <hr className="bg-dark" />

                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <p
                                style={{ fontSize: 13, color: '#000', fontWeight: 'bold' }}
                              >{`Total charge today`}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 13, color: '#000' }}>
                                {' '}
                                {currency}
                                {totalPrice}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  <div
                    className="p-4 border bg-white"
                    style={{
                      borderRadius: '10px',
                      overflow: 'hidden'
                    }}
                  >
                    <p style={{ fontSize: 13, color: '#000', fontWeight: 'bold' }}>
                      Payment details
                    </p>
                    <PaymentForm
                      applicationId={import.meta.env.VITE_SQUARE_APPLICATION_ID}
                      locationId={import.meta.env.VITE_SUBSCRIPTION_SQAURE_LOCATION_ID}
                      overrides={{
                        scriptSrc:
                          import.meta.env.VITE_SQUARE_ENV === 'production'
                            ? 'https://web.squarecdn.com/v1/square.js'
                            : 'https://sandbox.web.squarecdn.com/v1/square.js'
                      }}
                      cardTokenizeResponseReceived={handleCardToken}
                      createPaymentRequest={() => ({
                        countryCode: 'GB',
                        currencyCode: 'GBP',
                        total: { label: 'Seamless Slot', amount: '1.00' }
                      })}
                      createVerificationDetails={() => ({
                        amount: '1.00',
                        billingContact: {
                          addressLines: ['123 Main Street', 'Apartment 1'],
                          familyName: 'Doe',
                          givenName: 'John',
                          countryCode: 'GB',
                          city: 'London'
                        },
                        currencyCode: 'GBP',
                        intent: 'CHARGE'
                      })}
                    >
                      <CreditCard
                        focus=""
                        callbacks={{
                          // inputEventReceived: handleInputEvent,
                          postalCodeChanged: handleInputEvent,
                          focusClassRemoved: handleInputEvent,
                          errorClassAdded() {
                            setLoading(false);
                          }
                        }}
                      />

                      <Button
                        onClick={() => {
                          document.querySelector('#rswp-card-button').click();
                          setLoading(true);
                        }}
                        className="btn btn-dark w-100 mt-3"
                        disabled={loading}
                      >
                        {loading ? 'Processing...' : 'Upgrade Now'}
                      </Button>
                    </PaymentForm>
                  </div>
                </div>
              )}

              {/* Plan Upgraded Message */}
              {planUpgraded && (
                <div className="d-flex justify-content-center  ">
                  <div
                    className="bg-white rounded-4 p-5 text-center"
                    style={{ maxWidth: 520, width: '100%', height: '40%' }}
                  >
                    {/* Success Icon */}
                    <div
                      className="d-flex justify-content-center align-items-center mx-auto mb-4"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: '#5cc96b'
                      }}
                    >
                      <i className="bx bx-check  text-light" style={{ fontSize: 50 }}></i>
                    </div>

                    {/* Title */}
                    <h3 className="fw-bold mb-2">Payment Successful</h3>

                    {/* Description */}
                    <p className="text-muted mb-4">
                      You're all set. Your <strong>Pro Plan </strong> is active
                    </p>

                    <hr className="my-4" />

                    {/* Billing Info */}
                    {/* <p className="text-muted mb-4">
                    Next billing: <strong>4 December 2026</strong>
                  </p> */}

                    {/* Action Button */}
                    <Button
                      color="dark"
                      className="px-4 py-2"
                      onClick={() => {
                        onClose();
                        window.location.reload();
                      }}
                    >
                      Go to dashboard
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Container>
      </ModalBody>
      <style>{`
        #rswp-card-button { display:none }
      `}</style>
    </Container>
  );
}
